import React, { useState, useRef, useEffect } from 'react';
import { useMultiHOA } from '../MultiHOAContext';

/**
 * Dropdown component for switching between multiple HOAs
 * Shows current active HOA and allows selection of other HOAs
 */
export const HOASelectorDropdown = () => {
  const { activeHoaId, setActiveHoaId, userHoas } = useMultiHOA();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Don't show if user only has one HOA
  if (!userHoas || userHoas.length <= 1) {
    return null;
  }

  // Type-safe equality helper (handles string vs number comparison)
  const eq = (a, b) => String(a) === String(b);

  // Find current active HOA
  const activeHOA = userHoas.find(h => eq(h.hoa_id, activeHoaId));
  const activeHOAName = activeHOA?.hoa_name || activeHOA?.hoa_code || 'Select HOA';

  const handleSelectHOA = async (hoaId) => {
    console.log('🏘️ [SWITCHER] User clicked to switch to HOA:', hoaId);
    console.log('🏘️ [SWITCHER] Current activeHoaId:', activeHoaId);
    
    try {
      // Update local state FIRST before API call
      const hoaIdString = String(hoaId);
      console.log('🏘️ [SWITCHER] Setting activeHoaId in localStorage:', hoaIdString);
      setActiveHoaId(hoaIdString);
      
      // Verify it was saved
      const saved = localStorage.getItem('active_hoa_id');
      console.log('🏘️ [SWITCHER] Verified localStorage value:', saved);
      
      // Call backend to validate and set context
      console.log('🏘️ [SWITCHER] Calling backend set-context API...');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user-context/set-context`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('hoa_token')}`
        },
        body: JSON.stringify({ hoa_id: hoaIdString })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🏘️ [SWITCHER] Backend error:', response.status, errorText);
        throw new Error(`Failed to switch HOA context: ${response.status}`);
      }
      
      console.log('🏘️ [SWITCHER] Backend confirmed switch. Reloading page...');
      setIsOpen(false);
      
      // Reload the page to refresh all data with new HOA context
      window.location.reload();
    } catch (error) {
      console.error('🏘️ [SWITCHER] Error switching HOA:', error);
      alert(`Failed to switch HOA: ${error.message}. Please try again.`);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
        aria-label="Select HOA"
      >
        <span className="text-sm font-medium text-gray-700">
          🏘️ {activeHOAName}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="py-1">
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
              Your HOAs
            </div>
            {userHoas.map((hoa) => (
              <button
                key={hoa.hoa_id}
                onClick={() => handleSelectHOA(hoa.hoa_id)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                  hoa.hoa_id === activeHoaId ? 'bg-indigo-50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {hoa.hoa_name || hoa.hoa_code}
                    </div>
                    {hoa.role && (
                      <div className="text-xs text-gray-500 capitalize">
                        {hoa.role.replace('_', ' ')}
                      </div>
                    )}
                  </div>
                  {hoa.hoa_id === activeHoaId && (
                    <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HOASelectorDropdown;